import React, { useState } from 'react';

// Types for a single date-time input
export interface DateTimeInputProps {
  id?: string;
  name?: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  error?: string | null;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  min?: string;
  max?: string;
  step?: number;
}

// Generic DateTime Input Component
export const DateTimeInput: React.FC<DateTimeInputProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  error,
  required = false,
  disabled = false,
  className = '',
  inputClassName,
  placeholder,
  min,
  max,
  step = 900, // Default to 15 minutes (900 seconds)
}) => {
  // Default input class function
  const getDefaultInputClass = (hasError: boolean): string => {
    const baseClass =
      'w-full px-3 py-1 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

    if (disabled) {
      return `${baseClass} border-gray-300 bg-gray-100 cursor-not-allowed`;
    }

    if (hasError) {
      return `${baseClass} border-red-500`;
    }

    return `${baseClass} border-gray-300`;
  };

  const inputClass = inputClassName ?? getDefaultInputClass(!!error);

  return (
    <div className={`form-group ${className}`}>
      <label htmlFor={id ?? name} className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && '*'}
      </label>
      <input
        type="datetime-local"
        id={id ?? name}
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        className={inputClass}
        required={required}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

// Hook for managing a single date-time field with validation
export const useDateTimeField = (initialValue: string = '', validationFn?: (value: string) => string | null) => {
  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<boolean>(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.target.value;
    setValue(newValue);

    // Clear error when user starts typing
    if (error && newValue !== '') {
      setError(null);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    setTouched(true);
    validate(e.target.value);
  };

  const validate = (valueToValidate?: string): boolean => {
    const currentValue = valueToValidate !== undefined ? valueToValidate : value;

    if (validationFn) {
      const validationError = validationFn(currentValue);
      setError(validationError);
      return validationError === null;
    }

    return true;
  };

  const reset = (): void => {
    setValue(initialValue);
    setError(null);
    setTouched(false);
  };

  return {
    value,
    setValue,
    error,
    setError,
    touched,
    setTouched,
    handleChange,
    handleBlur,
    validate,
    reset,
    isValid: error === null,
  };
};

// Common validation functions
export const dateTimeValidators = {
  required: (value: string): string | null => {
    if (!value || value.trim() === '') {
      return 'This field is required';
    }
    return null;
  },

  futureDate: (value: string): string | null => {
    if (!value) return null;

    const selectedDate = new Date(value);
    const now = new Date();

    if (selectedDate <= now) {
      return 'Please select a future date and time';
    }
    return null;
  },

  pastDate: (value: string): string | null => {
    if (!value) return null;

    const selectedDate = new Date(value);
    const now = new Date();

    if (selectedDate >= now) {
      return 'Please select a past date and time';
    }
    return null;
  },

  minDate:
    (minDate: string) =>
    (value: string): string | null => {
      if (!value || !minDate) return null;

      const selectedDate = new Date(value);
      const minimumDate = new Date(minDate);

      if (selectedDate < minimumDate) {
        return `Date must be after ${minimumDate.toLocaleString()}`;
      }
      return null;
    },

  maxDate:
    (maxDate: string) =>
    (value: string): string | null => {
      if (!value || !maxDate) return null;

      const selectedDate = new Date(value);
      const maximumDate = new Date(maxDate);

      if (selectedDate > maximumDate) {
        return `Date must be before ${maximumDate.toLocaleString()}`;
      }
      return null;
    },

  combine:
    (...validators: Array<(value: string) => string | null>) =>
    (value: string): string | null => {
      for (const validator of validators) {
        const error = validator(value);
        if (error) return error;
      }
      return null;
    },
};

export default DateTimeInput;
